<template>
  <div>
    <TitleWrapper
      :filter-option="false"
      title="ACCEPT_PAYMENT"
      tooltip-title="ACCEPT_PAYMENT"
      :display-breadcrumb="true"
    />
    <div
      class="w-full flex md:flex-row flex-col md:justify-between justify-start md:items-center items-start mt-2"
    >
      <div class="font-medium font-roboto md:text-base text-sm text-label-text">
        Select Student:
      </div>
      <div class="w-[250px]">
        <UiSingleSelect
          v-model="selectedStudent"
          label="full_name"
          :hide-title="true"
          :options="studentsList"
          class="w-full mx-0 mt-1 md:mt-0 md:-mb-5 mb-0"
          :select-object-without-reduce-key="true"
          :search-through-api="true"
          @search="getStudentList"
          @change="updateStudent(selectedStudent.id)"
          @deSelect="updateStudent()"
        />
      </div>
    </div>
    <template v-if="isLoading"><Loader class="mt-20 mb-40" :content="true" /></template>
    <div v-else :key="selectedStudent" class="mt-2 relative">
      <UserDetailedView
        :detailed-view-is-not-open-in-modal="true"
        :user="userDetails"
        :default-value="GENERAL_CONSTANTS.BLANKED"
      />
      <div
        class="flex md:flex-row flex-col md:justify-between justify-start md:items-center items-start mb-2"
      >
        <div class="font-medium md:text-base text-sm">Select Fee:</div>
        <div class="w-[250px]">
          <UiMultiSelectBox
            v-model="selectedFee"
            :hide-title="true"
            :search-through-api="true"
            :options="pendingFeeList"
            class="mx-0 md:px-0 mt-1 md:mt-0 md:-mb-5 mb-0"
            input-color="bg-white"
            @search="loadPendingFeeList"
          />
        </div>
      </div>
      <!-- Selected Fee -->
      <div v-if="selectedFee.length">
        <ValidationObserver v-slot="{ handleSubmit }">
          <!-- Bulk Fee Accept -->

          <TableWrapper table-height="h-auto">
            <THead>
              <TRHead>
                <TH>Fee Title</TH>
                <TH v-if="!currentClassScope">{{ className.toLowerCase() }}</TH>
                <TH v-if="!currentSectionScope">
                  {{ pluralToSingular(sectionName.toLowerCase()) }}
                </TH>
                <TH v-for="head in tableHeaders" :key="head">
                  {{ head }}
                </TH>
              </TRHead>
            </THead>
            <TBody>
              <TRBody v-for="(student, index) in selectedFee" :key="index">
                <TD>{{ (student && student.title) || GENERAL_CONSTANTS.NOT_APPLICABLE }}</TD>
                <TD v-if="!currentClassScope">
                  {{ student.section.class.title || GENERAL_CONSTANTS.NOT_APPLICABLE }}
                </TD>
                <TD v-if="!currentSectionScope">
                  {{ student.section.title || GENERAL_CONSTANTS.NOT_APPLICABLE }}
                </TD>
                <TD>
                  {{
                    (student && $filters.formattedDate(student.due_date)) ||
                    GENERAL_CONSTANTS.NOT_APPLICABLE
                  }}
                </TD>
                <TD>{{ (student && student.fee_amount) || GENERAL_CONSTANTS.NOT_APPLICABLE }}</TD>
                <TD>
                  {{ (student && student.fee_type.title) || GENERAL_CONSTANTS.NOT_APPLICABLE }}
                </TD>
                <TD>
                  {{ (student && student.payable_amount) || GENERAL_CONSTANTS.NOT_APPLICABLE }}
                </TD>
                <TD>
                  <span>{{ student?.paid_amount || GENERAL_CONSTANTS.NOT_APPLICABLE }}</span>
                </TD>
                <TD>
                  <span>{{ student?.discount_amount || GENERAL_CONSTANTS.NOT_APPLICABLE }}</span>
                </TD>
                <TD>
                  <span>
                    {{ student?.discount_type?.title || GENERAL_CONSTANTS.NOT_APPLICABLE }}
                  </span>
                </TD>
                <TD>
                  <span>{{ student?.fine_amount || GENERAL_CONSTANTS.NOT_APPLICABLE }}</span>
                </TD>
                <TD class="pt-5 lg:px-6 px-3.5">
                  <InputBox
                    v-model.number="student.new_paid_amount"
                    type="text"
                    :name="`Receiving Amount ${index} ${student.id}`"
                    title="Receiving Amount"
                    label="Receiving Amount"
                    :disabled="!!acceptedFee"
                    :hide-title="true"
                    placeholder="Receiving Amount"
                    class="flex-1 w-max"
                    rules="required|numeric"
                  />
                </TD>
                <TD class="pt-5 lg:px-6 px-3.5">
                  <UiDatePicker
                    v-model="student.new_paid_date"
                    :name="`Received On ${index} ${student.id}`"
                    title="Received On"
                    label="Received On"
                    :disabled="!!acceptedFee"
                    :date-mode="DATE_MODE.END_DATE_TIME"
                    class="flex-1 w-[210px]"
                    :hide-title="true"
                    rules="required"
                  />
                </TD>
                <TD class="pt-5 lg:px-6 px-3.5">
                  <InputBox
                    :key="`input${forceRerender}`"
                    v-model.number="student.new_discount_amount"
                    type="text"
                    :name="`Discount Amount ${index} ${student.id}`"
                    title="Discount Amount"
                    label="Discount Amount"
                    :disabled="!!acceptedFee"
                    :hide-title="true"
                    placeholder="Discount Amount"
                    class="flex-1 w-max"
                    rules="numeric"
                  />
                </TD>
                <TD>
                  <UiSingleSelect
                    :key="`input${forceRerender}`"
                    v-model="student.new_discount_type_id"
                    title="Discount Type"
                    :name="`Discount Type ${index}${student.id}`"
                    :options="discountList"
                    class="flex-1 pt-5"
                    container-classes="w-[210px]"
                    reduce="id"
                    :hide-title="true"
                    :rules="student.new_discount_amount ? 'required' : ''"
                    :search-through-api="true"
                    @search="discountTypes"
                  />
                </TD>
                <TD>
                  <InputBox
                    v-model="student.payment_method"
                    type="text"
                    :disabled="!!acceptedFee"
                    :name="`Payment Method  ${index} ${student.id}`"
                    title="Payment Method"
                    label="Payment Method"
                    :hide-title="true"
                    :no-error="true"
                    placeholder="Payment Method"
                    class="flex-1 w-max"
                  />
                </TD>
                <TD>
                  <InputBox
                    v-model="student.payment_comment"
                    type="text"
                    :name="`PAYMENT_COMMENT  ${index} ${student.id}`"
                    title="PAYMENT_COMMENT"
                    :disabled="!!acceptedFee"
                    :hide-title="true"
                    :no-error="true"
                    label="PAYMENT_COMMENT"
                    placeholder="PAYMENT_COMMENT"
                    class="flex-1 w-max"
                  />
                </TD>
              </TRBody>
            </TBody>
          </TableWrapper>
          <Divider />
          <div class="flex justify-end gap-5">
            <button
              v-if="!acceptedFee"
              class="focus:outline-none focus:shadow-md text-base text-white font-roboto h-8 flex justify-center items-center primary-button"
              :disabled="!selectedStudent || !selectedFee.length"
              type="button"
              @click.prevent="handleSubmit(acceptPayment)"
            >
              <span>Accept</span>
            </button>
            <div v-else class="flex gap-5">
              <button
                class="focus:outline-none focus:shadow-md text-base text-white font-roboto h-8 rtl:ml-3 flex justify-center items-center primary-button-outline"
                @click.prevent="printInvoice"
              >
                Print
              </button>
              <button
                class="focus:outline-none focus:shadow-md text-base text-white font-roboto h-8 rtl:ml-3 flex justify-center items-center primary-button"
                @click.prevent="moveToNextFee"
              >
                Next
              </button>
            </div>
          </div>
        </ValidationObserver>
      </div>
    </div>
    <PrintPaidInvoice v-if="feeInvoice" />
  </div>
</template>

<script>
import TitleWrapper from '@components/TitleWrapper.vue'
import { mapActions, mapState, mapGetters } from 'vuex'
import Loader from '@components/BaseComponent/Loader.vue'
import isEmpty from 'lodash/isEmpty'
import GENERAL_CONSTANTS from '@src/constants/general-constants'
import { dropdownTopBottom, objectDeepCopy, pluralToSingular } from '@utils/generalUtil.js'
import UiSingleSelect from '@/src/components/UiElements/UiSingleSelect.vue'
import UserDetailedView from '@src/components/userDetailedView.vue'
import Divider from '@src/components/Divider.vue'
import InputBox from '@components/UiElements/UiInputBox.vue'
import UiDatePicker from '@components/UiElements/UiDatePicker.vue'
import { Form as ValidationObserver } from 'vee-validate'
import { DATE_MODE } from '@/src/constants/date-time-constants.js'
import PrintPaidInvoice from '@/src/components/fee/PrintPaidInvoice.vue'
import UiMultiSelectBox from '@src/components/UiElements/UiMultiSelect.vue'
import { formatDate } from '@utils/moment.util'
import { buildWhereQuery } from '@/src/utils/filters'
import FILTER_KEYS from '@src/constants/filter-constants.js'
import {
  TableWrapper,
  TBody,
  TD,
  TH,
  THead,
  TRBody,
  TRHead,
} from '@src/components/UiElements/TableElements/index.js'
import SectionCustomName from '../../../settings/system-settings/SectionCustomName.vue'

export default {
  components: {
    TitleWrapper,
    Loader,
    UiSingleSelect,
    UserDetailedView,
    Divider,
    InputBox,
    UiDatePicker,
    ValidationObserver,
    PrintPaidInvoice,
    TableWrapper,
    TBody,
    TD,
    UiMultiSelectBox,
    TH,
    THead,
    TRBody,
    TRHead,
  },
  data() {
    return {
      isEmpty,
      DATE_MODE,
      GENERAL_CONSTANTS,
      tableHeaders: [
        'Due Date',
        'Total Fee',
        'Fee Type',
        'Payable Amount',
        'Paid Amount',
        'Current Discount',
        'Current Discount Type',
        'Fine Amount',
        'Receving Amount',
        'Received On',
        'Discount Amount',
        'Discount Type',
        'Payment Method',
        'Payment Comment',
      ],
      studentsList: [],
      selectedStudent: null,
      selectedFee: [],
      pendingFeeList: [],
      pendingFeeMeta: {},
      paymentDetails: {
        paid_amount: '',
        payment_method: '',
        payment_comment: '',
        student_fee_id: '',
        paid_date: '',
        discount_type_id: '',
        discount_amount: '',
      },
      acceptedFee: null,
      forceRerender: 0,
    }
  },
  computed: {
    ...mapGetters('featureNaming', ['className', 'sectionName']),
    ...mapState({
      showModal: (state) => state.layout.showModal,
      isLoading: (state) => state.layout.isLoading,
      feeInvoice: (state) => state.fee.feeInvoice,
      currentCampusScope: (state) => state.layout.currentCampusScope,
      currentClassScope: (state) => state.layout.currentClassScope,
      currentSectionScope: (state) => state.layout.currentSectionScope,
      discountList: (state) => state.fee.discountTypes?.records || [],
    }),
    userDetails() {
      return { user_details: this.selectedStudent }
    },
  },
  watch: {
    currentSectionScope: {
      handler(val) {
        this.selectedStudent = null
        this.resetDataToDefault()
        this.getStudentList()
      },
    },
    currentCampusScope: {
      handler(val) {
        if (val) {
          this.resetDataToDefault()
          this.selectedStudent = null
          this.getStudentList()
        }
      },
    },
  },
  created() {
    this.setRightbarData()
  },
  mounted() {
    this.getStudentList()
    this.discountTypes()
  },
  methods: {
    pluralToSingular,
    dropdownTopBottom,
    discountTypes(query = '') {
      let payload = {
        $where: {
          ...buildWhereQuery(FILTER_KEYS.ILIKE, 'title', query),
        },
      }
      this.getDiscountTypes(payload)
    },
    getStudentList(query = '') {
      let payload = {
        search: query,
      }
      this.getPendingFeeStudentsList(payload).then((response) => {
        this.studentsList = response?.data?.records
      })
    },
    updateStudent(id) {
      this.resetDataToDefault()
      if (id) this.getStudentFee(id)
    },

    loadPendingFeeList(query) {
      let payload = {
        $where: {
          ...buildWhereQuery(FILTER_KEYS.ILIKE, 'title', query),
          ...buildWhereQuery(FILTER_KEYS.IN, 'student_ids', [this.selectedStudent?.id]),
        },
      }
      this.getReceivableFees(payload)
    },
    getStudentFee(id) {
      let payload = { $where: { ...buildWhereQuery(FILTER_KEYS.IN, 'student_ids', [id]) } }
      this.getReceivableFees(payload)
    },
    getReceivableFees(payload) {
      this.getStudentsReceivableFees(payload).then((response) => {
        this.pendingFeeList = objectDeepCopy(response?.data?.records)
        this.pendingFeeMeta = response?.data?.meta
        this.pendingFeeList.forEach((fee) => {
          fee['title'] = fee?.section_fee?.title || fee?.title || GENERAL_CONSTANTS.NOT_APPLICABLE
          fee['detail'] = `Created On : ${formatDate(fee.inserted_at)} `
        })
        this.getSingleStudentFeeStats(payload).then((statsResponse) => {
          this.setRightbarData(statsResponse.data)
        })
      })
    },
    setRightbarData(stats = {}) {
      const rightBarContent = {
        header: {
          title: 'ACCEPT_PAYMENT',
        },
        stats: [
          {
            title: GENERAL_CONSTANTS.STATS_AND_SUMMARY,
            categories: [
              {
                text: 'TOTAL_FEE_AMOUNT',
                value: stats?.total_fee_amount || GENERAL_CONSTANTS.EMPTY_RECORD,
                tooltipDescription: 'Total fee',
              },
              {
                text: 'TOTAL_PENDING_FEE',
                value: stats?.total_pending_amount || GENERAL_CONSTANTS.EMPTY_RECORD,
                tooltipDescription: 'Total pending fee',
              },
              {
                text: 'TOTAL_RECEIVED_FEE',
                value: stats?.total_received_amount || GENERAL_CONSTANTS.EMPTY_RECORD,
                tooltipDescription: 'Total received fee',
              },
              {
                text: 'TOTAL_DISCOUNT_AMOUNT',
                value: stats?.total_discount_amount || GENERAL_CONSTANTS.EMPTY_RECORD,
                tooltipDescription: 'Total discount amount',
              },
            ],
          },
        ],
      }
      this.setRightbarContent(rightBarContent)
    },
    resetDataToDefault() {
      this.pendingFeeList = []
      this.selectedFee = []
      this.acceptedFee = null
      this.forceRerender++
      window.scrollTo(0, 0)
      this.setRightbarData()
    },
    moveToNextFee() {
      this.selectedStudent = null
      this.resetDataToDefault()
      this.setRightbarData()
    },
    acceptPayment() {
      let acceptablePaymentDetails = this.selectedFee.map((item) => {
        const transformedFee = {
          paid_amount: item.new_paid_amount || 0,
          payment_method: item.payment_method || '',
          payment_comment: item.payment_comment || '',
          student_fee_id: item.id,
          paid_date: item.new_paid_date,
        }

        // Include additional fields if they are provided
        if (item.new_discount_amount) {
          transformedFee.discount_amount = item.new_discount_amount
          transformedFee.discount_type_id = item.new_discount_type_id
        }

        return transformedFee
      })
      let payload = { student_fees: acceptablePaymentDetails }
      this.acceptStudentFee(payload).then((res) => {
        this.acceptedFee = res?.data
        const payeeName = this.selectedFee[0]?.student?.first_name
        this.forceRerender++
        this.$store.commit('toast/NEW', {
          type: 'success',
          message: `${this.$t('Fees of')} ${payeeName} ${this.$t(
            'toast.is received successfully',
          )}`,
        })
      })
    },

    printInvoice() {
      this.$store.commit('fee/SET_SHOW_MODAL', true)
      this.$store.commit('fee/SET_FEE_INVOICE', this.acceptedFee)
    },
    ...mapActions('layout', ['setRightbarContent']),
    ...mapActions('fee', [
      'getPendingFeeStudentsList',
      'getDiscountTypes',
      'getStudentsReceivableFees',
      'acceptStudentFee',
      'getSingleStudentFeeStats',
    ]),
  },
}
</script>

<style lang="scss" module>
.paid {
  padding: 4px 12px;
  color: #267c26;
  background-color: #e9f2e9;
  border-radius: 3px;
}
.delay {
  padding: 4px 12px;
  color: #ffc201;
  background-color: #fff9e6;
  border-radius: 3px;
}
.due {
  padding: 4px 12px;
  color: #ea3535;
  background-color: #fce1e1;
  border-radius: 3px;
}
tr:nth-child(even) {
  background-color: var(--ghost-white);
}
.tableDropDown {
  top: 32px;
  right: -21px;
  &::before {
    position: absolute;
    top: -7px;
    right: 30px;
    width: 12px;
    height: 12px;
    content: '';
    background: var(--bg-white);
    border-top: 1px solid var(--primary-grey);
    border-left: 1px solid var(--primary-grey);
    transform: rotate(45deg);
  }
}
.info {
  top: 35px;
  border: 1px solid var(--text-color-info);
  &::before {
    position: absolute;
    top: -7px;
    right: 30px;
    width: 12px;
    height: 12px;
    content: '';
    background: var(--bg-white);
    border-top: 1px solid var(--text-color-info);
    border-left: 1px solid var(--text-color-info);
    transform: rotate(45deg);
  }
}
</style>
